import { useEffect, useState, useCallback } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import classNames from 'classnames';
import useModal from '@hooks/useModal';
import { Radio } from '@com/_atoms/Radio';
import { Bottomsheet } from '@com/modal/containers/Bottomsheet';

interface SortOption {
  label: string;
  value: string;
}

interface Props {
  title?: string;
  paramKey: string;
  options: SortOption[];
  disablePageNumberQuerySet?: boolean;
}

const SortBottomsheet = ({
  title,
  paramKey,
  options,
  disablePageNumberQuerySet = false,
}: Props) => {
  const { removeLastModal } = useModal();

  const router = useRouter();
  const pathname = usePathname();
  const params = useSearchParams();

  const [value, setValue] = useState<string | undefined>();

  useEffect(() => {
    setValue(params.get(paramKey) || undefined);
  }, [params, paramKey]);

  const applySort = useCallback(
    (nextValue?: string) => {
      const searchParams = new URLSearchParams(params.toString());

      if (nextValue) searchParams.set(paramKey, nextValue);
      else searchParams.delete(paramKey);

      if (!disablePageNumberQuerySet) {
        searchParams.set('pageNumber', '1');
      }

      router.push(`${pathname}?${searchParams.toString()}`);
      removeLastModal();
    },
    [params, pathname, router, paramKey, removeLastModal],
  );

  const handleChange = (val: string) => {
    const newValue = value === val ? undefined : val;
    setValue(newValue);
    applySort(newValue);
  };

  return (
    <Bottomsheet
      height="auto"
      hasCloseButton
      title={title || 'مرتب‌سازی'}
      titleClassName="select-none"
    >
      <div className="flex flex-col pb-4 px-4 select-none">
        {options.map((opt, index) => (
          <div
            key={opt.value}
            className={classNames(
              index !== options.length - 1 ? 'border-b border-blue-50' : '',
              'py-4',
            )}
            onClick={() => handleChange(opt.value)}
          >
            <Radio
              checked={value === opt.value}
              label={opt.label}
              icon={
                <span className="h-2 w-2 bg-surface-primary rounded-full" />
              }
            />
          </div>
        ))}
      </div>
    </Bottomsheet>
  );
};

export default SortBottomsheet;
